import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, RefreshCw, LogOut, ExternalLink, Loader2, AlertTriangle,
  CheckCircle2, Clock, Circle, Beaker, Search, Zap, Copy,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';

const STATE_META = {
  connected_live: { label: 'Live',          pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', icon: CheckCircle2 },
  in_progress:    { label: 'In progress',   pill: 'bg-amber-500/15 text-amber-300 border-amber-500/40',       icon: Clock },
  not_started:    { label: 'Not started',   pill: 'bg-white/[0.06] text-white/65 border-white/15',            icon: Circle },
  stale:          { label: 'Stale (orphan)',pill: 'bg-rose-500/15 text-rose-300 border-rose-500/45',          icon: AlertTriangle },
  mock:           { label: 'Mock',          pill: 'bg-sky-500/15 text-sky-300 border-sky-500/40',             icon: Beaker },
};

const FILTERS = [
  { key: 'all',            label: 'All' },
  { key: 'stale',          label: 'Stale only' },
  { key: 'in_progress',    label: 'In progress' },
  { key: 'connected_live', label: 'Live' },
  { key: 'not_started',    label: 'Not started' },
];

const StripeHealthAdmin = () => {
  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; }
  });
  const [authed, setAuthed] = useState(!!adminKey);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ rows: [], summary: {} });
  const [choiceTelemetry, setChoiceTelemetry] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [actingId, setActingId] = useState('');

  const fetchData = async (key) => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/stripe-health`, {
        headers: { 'X-Admin-Key': key },
      });
      setData(r.data);
      setAuthed(true);
      // Side-load telemetry choice summary (best-effort — never blocks main view)
      axios
        .get(`${API}/admin/telemetry/stripe-choice`, {
          headers: { 'X-Admin-Key': key },
        })
        .then((res) => setChoiceTelemetry(res.data))
        .catch(() => setChoiceTelemetry(null));
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        setError('Invalid admin key.');
        setAuthed(false);
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not load stripe-health data.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (adminKey) fetchData(adminKey); /* eslint-disable-next-line */ }, []);

  const onSubmitKey = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    try { localStorage.setItem(ADMIN_KEY_LS, keyInput.trim()); } catch {}
    setAdminKey(keyInput.trim());
    fetchData(keyInput.trim());
  };

  const onSignOut = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey('');
    setKeyInput('');
    setAuthed(false);
    setData({ rows: [], summary: {} });
  };

  const onForceFresh = async (rid, name) => {
    if (!window.confirm(`Force fresh Stripe link for "${name}"?\n\nThis will discard the current Stripe account ID. The restaurant must re-click "Connect with Stripe" — a brand-new Stripe account will be created.`)) return;
    setActingId(rid);
    try {
      await axios.post(`${API}/admin/stripe-health/${rid}/force-fresh-link`, {}, {
        headers: { 'X-Admin-Key': adminKey },
      });
      await fetchData(adminKey);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not force fresh link.');
    } finally {
      setActingId('');
    }
  };

  const onCopy = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch {}
  };

  const rows = useMemo(() => {
    let all = data.rows || [];
    if (filter !== 'all') all = all.filter((r) => r.state === filter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      all = all.filter((r) =>
        (r.restaurant_name || '').toLowerCase().includes(q) ||
        (r.owner_email || '').toLowerCase().includes(q) ||
        (r.stripe_account_id || '').toLowerCase().includes(q) ||
        (r.slug || '').toLowerCase().includes(q),
      );
    }
    return all;
  }, [data, filter, query]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4" data-testid="stripe-health-gate">
        <form onSubmit={onSubmitKey} className="w-full max-w-md rounded-2xl border border-yellow-500/30 bg-zinc-950 p-7">
          <div className="w-10 h-10 mb-4 rounded-xl bg-yellow-500/[0.10] border border-yellow-500/40 flex items-center justify-center">
            <Shield className="w-5 h-5 text-yellow-300" />
          </div>
          <h1 className="text-xl font-extrabold tracking-tight mb-1">Admin sign-in</h1>
          <p className="text-[13px] text-white/55 mb-5">Enter the NoFee admin key to view Stripe Connect health.</p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="X-Admin-Key"
            data-testid="stripe-health-key-input"
            className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/60 outline-none text-[14px] mb-3"
            autoFocus
          />
          {error && <p className="mb-3 text-[12.5px] text-rose-300">{error}</p>}
          <button type="submit" data-testid="stripe-health-key-submit" className="w-full px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[14px]">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white antialiased" data-testid="stripe-health-page">
      {/* Top bar */}
      <div className="sticky top-0 z-20 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/[0.10] border border-yellow-500/40 flex items-center justify-center">
              <Zap className="w-4 h-4 text-yellow-300" />
            </div>
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Admin</p>
              <h1 className="text-[15px] font-bold leading-none mt-0.5">Stripe health</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchData(adminKey)} disabled={loading} data-testid="stripe-health-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-white/25 text-white/80 hover:text-white text-[12.5px] font-semibold transition-all">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button onClick={onSignOut} data-testid="stripe-health-signout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-rose-500/40 text-white/55 hover:text-rose-300 text-[12.5px] font-semibold transition-all">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-7 pb-20">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="stripe-health-summary">
          {[
            { key: 'total',          label: 'Total',       value: s.total || 0,          Icon: Circle,         tone: 'border-white/12 bg-white/[0.02]' },
            { key: 'connected_live', label: 'Live',        value: s.connected_live || 0, Icon: CheckCircle2,   tone: 'border-emerald-500/30 bg-emerald-500/[0.05]' },
            { key: 'in_progress',    label: 'In progress', value: s.in_progress || 0,    Icon: Clock,          tone: 'border-amber-500/30 bg-amber-500/[0.05]' },
            { key: 'stale',          label: 'Stale',       value: s.stale || 0,          Icon: AlertTriangle,  tone: 'border-rose-500/30 bg-rose-500/[0.05]' },
            { key: 'not_started',    label: 'Not started', value: s.not_started || 0,    Icon: Circle,         tone: 'border-white/10 bg-white/[0.01]' },
          ].map(({ key, label, value, Icon, tone }) => (
            <div key={key} data-testid={`stripe-health-tile-${key}`} className={`p-4 rounded-2xl border ${tone}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-white/55" />
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">{label}</span>
              </div>
              <div className="mt-2 text-3xl font-extrabold tabular-nums">{value}</div>
            </div>
          ))}
        </div>

        {/* Telemetry — Stripe-onboard choice funnel */}
        {choiceTelemetry && (
          <div
            data-testid="stripe-health-telemetry-card"
            className="mt-5 p-5 sm:p-6 rounded-2xl border border-yellow-500/25 bg-gradient-to-br from-yellow-500/[0.04] via-zinc-950 to-black"
          >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                  Funnel · Stripe onboarding
                </p>
                <h2 className="mt-1 text-[15px] sm:text-base font-bold tracking-tight text-white">
                  Existing Stripe account vs. brand-new
                </h2>
              </div>
              <span className="text-[11.5px] text-white/45">
                Based on{' '}
                <span className="text-white/85 font-semibold tabular-nums">
                  {choiceTelemetry.total}
                </span>{' '}
                self-reported choice{choiceTelemetry.total === 1 ? '' : 's'}
              </span>
            </div>

            {choiceTelemetry.total === 0 ? (
              <p className="text-[13px] text-white/50">
                No funnel data yet — restaurants will start showing up here as they pick their option on the Stripe-connect step.
              </p>
            ) : (
              <>
                {/* Stacked bar */}
                <div className="h-2.5 rounded-full overflow-hidden bg-white/[0.05] flex">
                  <div
                    data-testid="telemetry-bar-existing"
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                    style={{ width: `${choiceTelemetry.existing_pct}%` }}
                  />
                  <div
                    data-testid="telemetry-bar-new"
                    className="h-full bg-gradient-to-r from-yellow-300 to-amber-500"
                    style={{ width: `${choiceTelemetry.new_pct}%` }}
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div
                    data-testid="telemetry-stat-existing"
                    className="p-4 rounded-xl border border-emerald-500/25 bg-emerald-500/[0.04]"
                  >
                    <div className="flex items-center gap-2 text-emerald-300 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Already had Stripe
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span className="text-3xl font-extrabold tabular-nums text-white">
                        {choiceTelemetry.existing}
                      </span>
                      <span className="text-[13px] font-bold text-emerald-300 tabular-nums">
                        {choiceTelemetry.existing_pct}%
                      </span>
                    </div>
                  </div>
                  <div
                    data-testid="telemetry-stat-new"
                    className="p-4 rounded-xl border border-yellow-500/25 bg-yellow-500/[0.04]"
                  >
                    <div className="flex items-center gap-2 text-yellow-300 text-[10.5px] uppercase tracking-[0.18em] font-bold">
                      <Zap className="w-3.5 h-3.5" />
                      Needed to create one
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <span className="text-3xl font-extrabold tabular-nums text-white">
                        {choiceTelemetry.new}
                      </span>
                      <span className="text-[13px] font-bold text-yellow-300 tabular-nums">
                        {choiceTelemetry.new_pct}%
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Filter + search */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between" data-testid="stripe-health-controls">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => {
              const active = f.key === filter;
              return (
                <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`stripe-health-filter-${f.key}`}
                  className={`px-3.5 py-1.5 rounded-full border text-[12px] font-bold transition-all ${
                    active ? 'bg-yellow-500/[0.10] border-yellow-500/45 text-yellow-200'
                           : 'border-white/10 hover:border-white/25 text-white/55 hover:text-white/80'
                  }`}>
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-3.5 h-3.5 text-white/35 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, acct_id…"
              data-testid="stripe-health-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-black/40 border border-white/12 focus:border-yellow-500/50 outline-none text-[12.5px]"
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 p-4 rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[13.5px]">{error}</div>
        )}

        {/* Rows */}
        <div className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
          {rows.length === 0 && !loading ? (
            <div className="p-10 text-center text-white/45 text-[14px]">No restaurants match this filter.</div>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {rows.map((r) => {
                const meta = STATE_META[r.state] || STATE_META.not_started;
                const Icon = meta.icon;
                return (
                  <li key={r.restaurant_id} data-testid={`stripe-health-row-${r.restaurant_id}`} className="p-4 sm:p-5 flex flex-col gap-3">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[15px] font-bold text-white truncate">{r.restaurant_name || '—'}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10.5px] uppercase tracking-wider font-bold ${meta.pill}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                          {r.is_active && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
                              Public
                            </span>
                          )}
                          {r.stripe_stale_email_sent_at && (
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-rose-500/10 border border-rose-500/30 text-rose-300" title={`Reconnect email sent at ${r.stripe_stale_email_sent_at}`}>
                              Reconnect email sent
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-white/55">
                          <span>{r.owner_email || '—'}</span>
                          {r.slug && <span>/{r.slug}</span>}
                          <span className="text-white/35">step: {r.onboarding_step || '—'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.state === 'stale' || (r.state === 'in_progress' && r.stripe_account_id) ? (
                          <button onClick={() => onForceFresh(r.restaurant_id, r.restaurant_name)} disabled={actingId === r.restaurant_id}
                            data-testid={`stripe-health-force-fresh-${r.restaurant_id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-500/30 hover:border-rose-500/55 bg-rose-500/[0.06] hover:bg-rose-500/[0.10] text-rose-200 text-[11.5px] font-bold transition-all disabled:opacity-50">
                            {actingId === r.restaurant_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Force fresh link
                          </button>
                        ) : null}
                      </div>
                    </div>

                    {(r.stripe_account_id || r.requirements?.length) && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11.5px]">
                        {r.stripe_account_id && (
                          <div className="p-2.5 rounded-lg bg-black/30 border border-white/[0.06] flex items-center justify-between gap-2 min-w-0">
                            <div className="min-w-0">
                              <div className="text-white/40 uppercase tracking-[0.16em] text-[9.5px] font-bold mb-0.5">acct_id</div>
                              <div className="font-mono text-white/85 truncate">{r.stripe_account_id}</div>
                            </div>
                            <button onClick={() => onCopy(r.stripe_account_id)} className="text-white/50 hover:text-yellow-300 shrink-0" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
                          </div>
                        )}
                        <div className="p-2.5 rounded-lg bg-black/30 border border-white/[0.06]">
                          <div className="text-white/40 uppercase tracking-[0.16em] text-[9.5px] font-bold mb-0.5">capabilities</div>
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                            <span className={r.charges_enabled ? 'text-emerald-300' : 'text-white/40'}>charges {r.charges_enabled ? '✓' : '×'}</span>
                            <span className={r.payouts_enabled ? 'text-emerald-300' : 'text-white/40'}>payouts {r.payouts_enabled ? '✓' : '×'}</span>
                            <span className={r.details_submitted ? 'text-emerald-300' : 'text-white/40'}>details {r.details_submitted ? '✓' : '×'}</span>
                          </div>
                        </div>
                        {r.requirements?.length ? (
                          <div className="p-2.5 rounded-lg bg-amber-500/[0.05] border border-amber-500/25 sm:col-span-1">
                            <div className="text-amber-300/80 uppercase tracking-[0.16em] text-[9.5px] font-bold mb-0.5">missing</div>
                            <div className="text-amber-200/90 line-clamp-2" title={r.requirements.join(', ')}>{r.requirements.slice(0, 3).join(', ')}{r.requirements.length > 3 ? ` +${r.requirements.length - 3}` : ''}</div>
                          </div>
                        ) : null}
                        {r.stripe_account_id?.startsWith('acct_') && !r.stripe_account_id.startsWith('acct_mock_') && (
                          <a href={`https://dashboard.stripe.com/connect/accounts/${r.stripe_account_id}`} target="_blank" rel="noreferrer"
                            className="p-2.5 rounded-lg bg-black/30 border border-white/[0.06] hover:border-yellow-500/30 text-white/75 hover:text-white inline-flex items-center justify-between gap-2 transition-all">
                            <span className="uppercase tracking-[0.16em] text-[9.5px] font-bold text-white/50">Open in Stripe</span>
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="mt-5 text-[11.5px] text-white/40 leading-relaxed max-w-2xl">
          <strong className="text-white/65">Force fresh link:</strong> discards the restaurant's stored Stripe account ID. The next time they click "Connect with Stripe", a brand-new Stripe account is created. Use this for stale / orphaned accounts that block the flow.
        </p>
      </div>
    </div>
  );
};

export default StripeHealthAdmin;
